import React, { useCallback } from "react";
import {
  Box,
  Switch,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { isSystemLockedSection, systemConstraints } from "../doctor-console/config";
import {
  sortByOrder,
  reorderByIndex,
  useDragReorder,
  dragRowSx,
  dragListContainerSx,
} from "./reorderUtils";
import SubSectionsEditor from "./SubSectionsEditor.js";
import SectionFieldsEditor from "./SectionFieldsEditor.js";
import { MANAGER_COLORS, accordionSx, fhirChipSx, helperCaptionSx } from "./managerTheme.js";

const SectionsEditor = ({ sections, fieldSets, onChange, onFieldSetChange }) => {
  const sorted = sortByOrder(sections);

  const updateSection = (id, patch) => {
    onChange(sections.map((section) => (section.id === id ? { ...section, ...patch } : section)));
  };

  const handleSectionReorder = useCallback(
    (dragIndex, dropIndex) => {
      onChange(reorderByIndex(sections, dragIndex, dropIndex));
    },
    [sections, onChange],
  );

  const { handleDragStart, handleDragOver, handleDrop } = useDragReorder(handleSectionReorder);

  const updateSubSections = (sectionId, subSections) => {
    updateSection(sectionId, { subSections });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <ViewListOutlinedIcon sx={{ fontSize: 18, color: MANAGER_COLORS.primary }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
          Console sections
        </Typography>
        <Chip
          size="small"
          label={`${sorted.filter((s) => s.id !== "attachments").length} sections`}
          sx={{ height: 22, fontSize: "0.7rem", bgcolor: MANAGER_COLORS.accentLight }}
        />
      </Stack>
      <Typography sx={helperCaptionSx}>
        Drag rows to reorder. Expand a section to configure its fields or history sub-sections.
      </Typography>

      <Box sx={dragListContainerSx}>
        {sorted.map((section, index) => {
          // Attachments is hidden from the manager UI (kept in `sections`/`sorted` so
          // drag-reorder indices for the remaining rows stay aligned with reorderByIndex's
          // sorted array); the section itself is untouched and still renders normally
          // in the actual Doctor Console.
          if (section.id === "attachments") {
            return null;
          }

          const locked = isSystemLockedSection(section.id);
          const constraint = systemConstraints.sections?.[section.id];
          const isHistory = section.renderer?.type === "historyContainer";
          const fieldSet = section.fieldSet ? fieldSets?.[section.fieldSet] : null;
          const subFieldSetEntries = section.subFieldSets
            ? Object.entries(section.subFieldSets)
            : [];

          return (
            <Box
              key={section.id}
              sx={{
                borderBottom: index < sorted.length - 1 ? `1px solid ${MANAGER_COLORS.borderLight}` : "none",
                backgroundColor: MANAGER_COLORS.card,
              }}
            >
              <Box
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                sx={{ ...dragRowSx, borderBottom: "none", flexWrap: "wrap" }}
              >
                <DragIndicatorIcon fontSize="small" sx={{ color: MANAGER_COLORS.primary, flexShrink: 0 }} />

                <Tooltip
                  title={
                    locked
                      ? constraint?.reason ?? "This section is required and cannot be disabled."
                      : "Enable/disable this section"
                  }
                >
                  <span>
                    <Switch
                      size="small"
                      checked={section.enabled !== false}
                      disabled={locked}
                      onChange={(e) => updateSection(section.id, { enabled: e.target.checked })}
                    />
                  </span>
                </Tooltip>

                {locked && (
                  <Tooltip title={constraint?.reason ?? "Locked (required for FHIR mapping)"}>
                    <LockIcon fontSize="small" sx={{ color: MANAGER_COLORS.primary, opacity: 0.5 }} />
                  </Tooltip>
                )}

                <Typography variant="body2" sx={{ minWidth: 180, flex: 1, fontWeight: 500 }}>
                  {section.label ?? section.key}
                </Typography>

                {section.system?.fhirResource && (
                  <Chip
                    size="small"
                    label={`FHIR: ${section.system.fhirResource}`}
                    variant="outlined"
                    sx={fhirChipSx}
                  />
                )}
              </Box>

              {fieldSet && (
                <Accordion disableGutters elevation={0} sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: MANAGER_COLORS.primary }} />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Fields — {section.label ?? section.key}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({sortByOrder(fieldSet.fields ?? []).length})
                      </Typography>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, px: 2, bgcolor: MANAGER_COLORS.card }}>
                    <SectionFieldsEditor
                      fieldSetId={section.fieldSet}
                      fieldSet={fieldSet}
                      onChange={onFieldSetChange}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              {subFieldSetEntries.map(([subKey, fieldSetId]) => {
                const subFieldSet = fieldSets?.[fieldSetId];
                if (!subFieldSet) return null;
                return (
                  <Accordion key={subKey} disableGutters elevation={0} sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: MANAGER_COLORS.primary }} />}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Fields — {subKey}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({sortByOrder(subFieldSet.fields ?? []).length})
                        </Typography>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2, bgcolor: MANAGER_COLORS.card }}>
                      <SectionFieldsEditor
                        fieldSetId={fieldSetId}
                        fieldSet={subFieldSet}
                        onChange={onFieldSetChange}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {isHistory && (
                <Accordion disableGutters elevation={0} sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: MANAGER_COLORS.primary }} />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      History sub-sections
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({sortByOrder(section.subSections ?? []).length})
                      </Typography>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, px: 2, bgcolor: MANAGER_COLORS.card }}>
                    <SubSectionsEditor
                      subSections={section.subSections ?? []}
                      fieldSets={fieldSets}
                      onChange={(subSections) => updateSubSections(section.id, subSections)}
                      onFieldSetChange={onFieldSetChange}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectionsEditor;
